import { useRouter } from 'next/router';
import {
  ChevronRightIcon,
  FlagIcon,
  NoElectricalConnectionIcon,
  NoGasConnectionIcon,
} from '@ads-core/icons';
import { Box, Stack, TextLink, Text, StackItem, Dialog, ProgressHero } from '@ads-core/components';
import {
  outageEndDateAndTime,
  outageFormatTexts,
  isOutageCause,
  setReplaceText,
  baseQueryParams,
} from 'src/utils';
import { IntegrationApiFeaturesOutagesContractsDataOutage } from '@alliander-fe/api';
import React from 'react';

import { OutageDialogContent } from '../OutageModal';
import { PolygonMap } from '../PolygonMap';

import { setStatus } from './utils/setStatus';
import { setStatusVariants } from './utils/setStatusVariants';
import { getOutageStrings } from './utils/getOutageStrings';
import { OutageHeroFollowupSteps } from './_ViewFollowUpSteps';

import { Props } from '.';

type OutageViewProps = {
  isLoading: boolean;
  postalCode?: string;
  outageData: IntegrationApiFeaturesOutagesContractsDataOutage;
  isSuccess: boolean;
} & Props;

export function OutageView(props: OutageViewProps) {
  const { outageData, isSuccess, isLoading, postalCode = '', ...rest } = props;
  const [dialogIsOpen, setDialogIsOpen] = React.useState<boolean>(false);
  const router = useRouter();

  const path = [...(router.query.path || '')].join('/');
  const baseUrl = window.location.origin || '';

  const params = new URLSearchParams();
  params.set(baseQueryParams.reference, outageData.outageNumber?.toString() || '');
  const queryString = params.toString();
  const outageDetailUrl = {
    href: `${baseUrl}/${path}?${queryString}`,
    target: '_blank',
  };

  const resolvedOutages = outageData.status?.toLowerCase() === 'opgelost';
  const isMaintenance = !isOutageCause(outageData.cause);
  const texts = getOutageStrings(props, outageData, !!postalCode);

  const data = {
    type: outageData.energyType?.toLowerCase() || '',
    places: outageData.affectedPlaces || '',
    postalCode: postalCode,
  };

  const status = setStatus({
    status: outageData.status,
    cause: outageData.cause,
    ...rest,
  });

  const phases = setStatusVariants({
    cause: outageData.cause,
    maintenancePhaseOneLabel: rest.maintenancePhaseOneLabel,
    maintenancePhaseTwoLabel: rest.maintenancePhaseTwoLabel,
    maintenancePhaseThreeLabel: rest.maintenancePhaseThreeLabel,
    outagePhaseOneLabel: rest.outagePhaseOneLabel,
    outagePhaseTwoLabel: rest.outagePhaseTwoLabel,
    outagePhaseThreeLabel: rest.outagePhaseThreeLabel,
  });

  const title = outageFormatTexts(texts.title || '', data);
  const detailTitle = outageFormatTexts(texts.detailTitle || '', data);
  const modalTitle = outageFormatTexts(texts.modalTitle || '', data);
  const detailText = outageFormatTexts(status?.text || '', data);

  const topTasks = isSuccess ? props.topTasks : props.noOutageTopTasks;

  const endDateAndTime = outageEndDateAndTime({
    ...outageData,
    resolvedOutages,
  });

  // We only show the disclaimer text incase of a outage.
  const outageDisclaimerText = !isMaintenance ? rest.outageDetailsModalText : undefined;

  const affectedPostalCodes = outageData.affectedPostalCodes
    ? setReplaceText(outageData.affectedPostalCodes, { ';': ', ' })
    : '';

  const affectedStreets = outageData.affectedStreets
    ? setReplaceText(outageData.affectedStreets, { ';': ', ' })
    : '';

  // TODO: Should we render a Skeleton if isLoading is true?
  return (
    <>
      <ProgressHero
        detailDescription={detailText}
        detailTitle={detailTitle}
        status={status?.label ?? ''}
        statusOptions={phases}
        title={title}
        topTasks={topTasks ?? []}
        progressLink={
          <Dialog
            handleOpenChange={setDialogIsOpen}
            isOpen={dialogIsOpen}
            trigger={
              <TextLink afterIcon={<ChevronRightIcon />} asChild>
                <button>Bekijk alle details</button>
              </TextLink>
            }
          >
            <OutageDialogContent
              handleClose={() => setDialogIsOpen(false)}
              {...outageData}
              cause={outageData.cause}
              status={outageData.status}
              statusDescription={status?.text ?? ''}
              statusPhases={phases}
              title={modalTitle}
              outageNumberLink={outageDetailUrl}
              outageDetailsText={
                resolvedOutages ? rest.noOutagesDetailsModalText : outageDisclaimerText
              }
              outageDetailsLinkOne={rest.outageDetailsModalLinkOne}
              outageDetailsLinkTwo={rest.outageDetailsModalLinkTwo}
              affectedPostalCodes={affectedPostalCodes}
              affectedStreets={affectedStreets}
            />
          </Dialog>
        }
        image={
          <Stack isFullHeight isFullWidth asChild>
            <Box borderRadius="brandXl" overflow="hidden">
              <PolygonMap
                postalCode={postalCode}
                rings={outageData.rings ?? undefined}
                isLoading={isLoading}
              />
            </Box>
          </Stack>
        }
        explanation={
          <Box paddingTop={{ initial: 4, md: 0 }} asChild>
            <Stack alignY="end" gap={2}>
              {isSuccess ? (
                <>
                  <Stack direction="row" gap={2}>
                    <StackItem>
                      {outageData.energyType === 'Stroom' ? (
                        <NoElectricalConnectionIcon size="xs" />
                      ) : (
                        <NoGasConnectionIcon size="xs" />
                      )}
                    </StackItem>
                    <Stack direction="column" gap={1} wrap>
                      <Text weight="semiBold">
                        {isMaintenance ? 'Start werkzaamheden:' : 'Storing gemeld:'}
                      </Text>
                      <Text>{outageData.reportTime}</Text>
                    </Stack>
                  </Stack>
                  {endDateAndTime ? (
                    <Stack direction="row" gap={2}>
                      <StackItem>
                        <FlagIcon size="xs" />
                      </StackItem>
                      <Stack direction="column" gap={1} wrap>
                        <Text weight="semiBold">{endDateAndTime.label}</Text>
                        <Text>{endDateAndTime.description}</Text>
                      </Stack>
                    </Stack>
                  ) : null}
                </>
              ) : null}
              {!outageData.outageNumber && props.noOutageLink?.href ? (
                <Stack direction="row" gap={2}>
                  <NoElectricalConnectionIcon size="xs" />
                  <TextLink href={props.noOutageLink.href}>{props.noOutageLink.text}</TextLink>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        }
      />
      {outageData.energyType && (
        <OutageHeroFollowupSteps energyType={outageData.energyType} {...props} />
      )}
    </>
  );
}
